import { useState, useEffect } from 'react'
import { getCsrfToken } from '../../utils/csrf'
import { UserData } from '../models/ModelAccount';


export function useAccount() {
  const [userData, setUserData] = useState<UserData | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isFormChanged, setIsFormChanged] = useState(false)

  useEffect(() => {
    fetchUserData()
  }, [])

  const fetchUserData = async () => {
    try {
      const csrfToken = await getCsrfToken()
      const response = await fetch('/api/account/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch user data')
      }

      const result = await response.json()
      if (result.success) {
        setUserData(result.user)
      }
    } catch (error) {
      setErrorMessage('Failed to load user data')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const csrfToken = await getCsrfToken()
      const response = await fetch('/api/account/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify(userData),
      })

      if (!response.ok) {
        throw new Error('Failed to update user data')
      }

      setIsEditing(false)
    } catch (error) {
      setErrorMessage('Failed to update user data')
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setUserData(prev => prev ? { ...prev, [name]: value } : null)
    setIsFormChanged(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setIsFormChanged(false)
    fetchUserData()
  }

  const formatMembershipDuration = (dateJoined: string): string => {
    const joinDate = new Date(dateJoined);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - joinDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays >= 365) {
      const years = Math.floor(diffDays / 365);
      const remainingDays = diffDays % 365;
      return `${years} ${years === 1 ? 'year' : 'years'} and ${remainingDays} ${remainingDays === 1 ? 'day' : 'days'}`;
    }
    
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
  }

  return {
    userData,
    isEditing,
    errorMessage,
    isFormChanged,
    handleSubmit,
    handleInputChange,
    handleCancel,
    setIsEditing,
    formatMembershipDuration
  }
}